import {
	IconButton,
	Stack,
	SvgIcon,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TablePagination,
	TableRow,
	Typography,
} from "@mui/material";
import PencilIcon from "@heroicons/react/24/solid/PencilIcon";
import DeleteButtonIcon from "./DeleteButtonIcon";
import type { useLinkRemover } from "../hook/useLinkRemover";
import type { ILink } from "../../entities/link.entity";
import { useGroupsList } from "../hook/useGroupsList";

interface LinkTableProps {
	count: number;
	items: ILink[];
	onPageChange: (event, value: number) => void;
	onRowsPerPageChange: (event) => void;
	linkRemover: ReturnType<typeof useLinkRemover>;
	onUpdateLink: (id: string) => void;
	page: number;
	rowsPerPage: number;
}

export default function LinkTable(props: LinkTableProps) {
	const {
		count = 0,
		items = [] as ILink[],
		onPageChange = () => {},
		onRowsPerPageChange,
		linkRemover,
		onUpdateLink,
		page = 0,
		rowsPerPage = 0,
	} = props;

	const groupsQuery = useGroupsList();

	const getSubCategoryPaths = (subCategoryPaths?: string[]) => {
		if (!subCategoryPaths || subCategoryPaths.length === 0) return "";
		return subCategoryPaths.join(", ");
	};

	const getGroupNames = (groupIds: string[]) => {
		if (!groupIds) return "";

		if (!groupsQuery.data) return groupIds.join(", ") || "";
		
		const groupNames = groupIds.map(id => {
			const group = groupsQuery.data.find(group => group.id === id);
			return group?.name || id;
		});
		
		return groupNames.join(", ");
	};

	return (
		<>
			<Table>
				<TableHead>
					<TableRow>
						<TableCell>Label</TableCell>
						<TableCell>URL</TableCell>
						<TableCell>Caminhos de Subcategorias</TableCell>
						<TableCell>Grupos</TableCell>
						<TableCell size="small">Ação</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{items.map((link) => {
						return (
							<TableRow hover key={link.id}>
								<TableCell>
									<Stack alignItems="center" direction="row" spacing={2}>
										<Typography variant="subtitle2">{link.label}</Typography>
									</Stack>
								</TableCell>
								<TableCell>
									<Typography variant="body2" color="textSecondary">
										{link.url}
									</Typography>
								</TableCell>
								<TableCell>
									<Typography variant="body2" color="textSecondary">
										{getSubCategoryPaths(link.subCategoryPaths)}
									</Typography>
								</TableCell>
								<TableCell>
									<Typography variant="body2" color="textSecondary">
										{getGroupNames(link.groupIds)}
									</Typography>
								</TableCell>
								<TableCell size="small">
									<Stack
										sx={{
											display: "inline-flex",
											flexDirection: "row",
											gap: "10px",
										}}
									>
										<IconButton
											aria-label="edit"
											color="primary"
											onClick={() => onUpdateLink(link.id)}
										>
											<SvgIcon>
												<PencilIcon />
											</SvgIcon>
										</IconButton>
										<DeleteButtonIcon
											onClick={() => linkRemover.mutate(link)}
											loading={
												linkRemover.isLoading &&
												linkRemover.variables?.id === link.id
											}
											disabled={linkRemover.isLoading}
										/>
									</Stack>
								</TableCell>
							</TableRow>
						);
					})}
				</TableBody>
			</Table>
			<TablePagination
				component="div"
				count={count}
				onPageChange={onPageChange}
				onRowsPerPageChange={onRowsPerPageChange}
				page={page}
				rowsPerPage={rowsPerPage}
				rowsPerPageOptions={[5, 10, 25]}
			/>
		</>
	);
}
